import {
  SERVER_URL,
  MAPI_URL,
  syncedGet,
  syncedSet,
  beginUiEdit,
  endUiEdit,
} from '../data/serverStore';
import React, { useState, useMemo, useEffect } from 'react';
import { getApiSettings } from './ApiSettings';
import { LOGO_BASE64 } from '../data/logo';
import { db } from '../data/store';
import { getDefaultUnit, getParamMeta } from '../data/params';
import { contactEmails, getCompanyContacts, normalizeContacts } from '../data/clientProfiles';

const today = () => new Date().toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });

const compareSamplesById = (a, b) =>
  String(a?.id ?? '').localeCompare(String(b?.id ?? ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
const sortSamplesById = samples => [...(samples || [])].sort(compareSamplesById);

// Results are retained as the laboratory's historical data, even after a test
// is removed from the sample setup. Reports, however, must reflect the
// parameters currently assigned to the sample. This keeps "remove from
// report" separate from "delete stored result". Samples created before the
// params field existed retain the legacy behavior and report all saved rows.
const getReportParams = sample => {
  const savedResults = sample?.results || {};
  const savedNames = Object.keys(savedResults);
  if (!Array.isArray(sample?.params)) return savedNames;

  const seen = new Set();
  return sample.params.filter(param => {
    const name = String(param || '').trim();
    if (!name || seen.has(name) || !Object.prototype.hasOwnProperty.call(savedResults, name)) return false;
    seen.add(name);
    return true;
  });
};

const getReportResults = sample =>
  getReportParams(sample).map(param => sample.results?.[param] || {});

// Show As Received results with the same decimal precision entered for the
// corresponding Dry Basis result. The saved calculation may retain additional
// precision, but reports should follow the technician's reporting precision.
const enteredDecimalPlaces = value => {
  const text = String(value ?? '').trim().replace(/,/g, '');
  const match = text.match(/^[+-]?(?:\d+(?:\.(\d*))?|\.([0-9]+))(?:e([+-]?\d+))?$/i);
  if (!match) return 4;
  const fractionLength = (match[1] || match[2] || '').length;
  const exponent = Number(match[3] || 0);
  return Math.max(0, Math.min(10, fractionLength - exponent));
};

const formatAsReceived = (value, dryBasisValue) => {
  if (value === '' || value === null || value === undefined) return '';
  const number = Number(value);
  return Number.isFinite(number)
    ? number.toFixed(enteredDecimalPlaces(dryBasisValue))
    : String(value);
};

// Resolve a per-parameter field (method/digestionMethod/spec/etc.) with
// fallback to admin defaults — but ONLY when the tech never touched the
// field at all. Plain `r.field || fallback` can't tell the difference
// between "never set" (undefined) and "deliberately cleared" (''), since
// both are falsy — so clearing a value in Enter Results just silently fell
// back to the admin default again instead of actually clearing it.
const resolveField = (explicit, ...fallbacks) =>
  explicit !== undefined ? explicit : (fallbacks.find(Boolean) || '');

const matrixMeta = (meta, sample, field) => {
  if (!meta) return '';
  if (sample?.solidLiq === 'S') return meta[`${field}Solid`] ?? meta[field] ?? '';
  if (sample?.solidLiq === 'T') return meta[`${field}Tclp`] ?? '';
  return meta[`${field}Liquid`] ?? meta[field] ?? '';
};

// Digestion and extraction are optional preparation steps assigned to selected
// tests for a particular sample. Unlike analytical method/specification fields,
// they must never fall back to parameter defaults in a printed report.
const resolvePreparationField = (explicit, assigned) =>
  assigned === true ? (explicit || '') : '';


// ── Persisted comment library + per-sample notes/photos ───────────────────
// Same defensive stringify/parse-clone pattern already used for adminMeta
// below and in EnterResults.jsx's loadPrefs, kept consistent on purpose.
const loadSynced = (key, fallback) => {
  try {
    const v = syncedGet(key, null);
    return v !== null ? JSON.parse(JSON.stringify(v)) : fallback;
  } catch { return fallback; }
};
const saveSynced = (key, val) => { try { syncedSet(key, val); } catch {} };

const loadCommentLibrary = () => loadSynced('sl_comment_library', []);
const saveCommentLibrary = (arr) => saveSynced('sl_comment_library', arr);

const loadSampleComment = (sampleId) => (loadSynced('sl_sample_comments', {})[sampleId] || '');
const saveSampleComment = (sampleId, text) => {
  const m = loadSynced('sl_sample_comments', {});
  m[sampleId] = text;
  saveSynced('sl_sample_comments', m);
};

const loadSamplePhotos = (sampleId) => (loadSynced('sl_sample_photos', {})[sampleId] || { before:null, after:null });
const saveSamplePhotos = (sampleId, photos) => {
  const m = loadSynced('sl_sample_photos', {});
  m[sampleId] = photos;
  saveSynced('sl_sample_photos', m);
};

// ── Evaluate a result against its specification ────────────────────────────
// Returns 'pass' | 'less' | 'more' | null (null = spec too free-form to
// parse, or nothing to compare — deliberately doesn't guess in that case).
// Handles a numeric range ("1.9-4.1"), a max/less-than spec ("max 5", "<5",
// "≤5"), or a min/greater-than spec ("min 5", ">5", "≥5").
function evaluateSpec(valueStr, specStr) {
  if (!valueStr || !specStr) return null;
  const val = parseFloat(valueStr);
  if (isNaN(val)) return null;

  const rangeMatch = specStr.match(/(-?\d+\.?\d*)\s*-\s*(-?\d+\.?\d*)/);
  if (rangeMatch) {
    const lo = parseFloat(rangeMatch[1]), hi = parseFloat(rangeMatch[2]);
    if (!isNaN(lo) && !isNaN(hi)) {
      if (val < lo) return 'less';
      if (val > hi) return 'more';
      return 'pass';
    }
  }
  const maxMatch = specStr.match(/(?:max|maximum|<=?|≤)\s*(-?\d+\.?\d*)/i);
  if (maxMatch) {
    const max = parseFloat(maxMatch[1]);
    if (!isNaN(max)) return val > max ? 'more' : 'pass';
  }
  const minMatch = specStr.match(/(?:min|minimum|>=?|≥)\s*(-?\d+\.?\d*)/i);
  if (minMatch) {
    const min = parseFloat(minMatch[1]);
    if (!isNaN(min)) return val < min ? 'less' : 'pass';
  }
  return null;
}
const VERDICT_LABEL = { pass: 'Pass', fail: 'Fail', less: 'Less Than', more: 'More Than' };
const VERDICT_COLOR = { pass: '#1a6b42', fail: '#b83232', less: '#b83232', more: '#b83232' };

// ── Dynamic report appendix ────────────────────────────────────────────────
// Every printed report ends with one definitions page. Only terms that
// actually appear in that report are included, so a report without an RL,
// DF, Wet Basis, QA/QC, EPA method, etc. does not define unused terminology.
const REPORT_GLOSSARY = [
  ['RL',       'Reporting Limit — the lowest concentration the laboratory routinely reports for the method and sample matrix.'],
  ['RDL',      'Reporting Detection Limit — the lowest concentration reported for the method and sample matrix.'],
  ['DF',       'Dilution Factor — the numerical factor applied when a prepared sample or extract is diluted before analysis.'],
  ['AR',       'As Received — result calculated on the sample in the condition received by the laboratory.'],
  ['QA/QC',    'Quality Assurance / Quality Control.'],
  ['MDL',      'Method Detection Limit.'],
  ['Tech',     'Laboratory technician.'],
  ['PO',       'Purchase Order.'],
  ['Dup.',     'Duplicate.'],
  ['Std',      'Standard.'],
  ['Alt',      'Alternative.'],
  ['ICP',      'Inductively Coupled Plasma.'],
  ['EPA',      'United States Environmental Protection Agency.'],
  ['ASTM',     'ASTM International, formerly the American Society for Testing and Materials.'],
  ['ASTM D975','Standard Specification for Diesel Fuel Oils.'],
  ['TCLP',     'Toxicity Characteristic Leaching Procedure.'],
  ['SW-846',   'EPA Test Methods for Evaluating Solid Waste, Physical/Chemical Methods.'],
  ['ND',       'Not Detected.'],
  ['N/A',      'Not Applicable.'],
  ['mg/L',     'Milligrams per liter.'],
  ['mg/kg',    'Milligrams per kilogram.'],
  ['µg/L',     'Micrograms per liter.'],
  ['µg/kg',    'Micrograms per kilogram.'],
  ['ppm',      'Parts per million.'],
  ['ppb',      'Parts per billion.'],
  ['%',        'Percent.'],
  ['°C',       'Degrees Celsius.'],
  ['°F',       'Degrees Fahrenheit.'],
  ['cSt',      'Centistokes, a unit of kinematic viscosity.'],
  ['BTU/lb',   'British thermal units per pound.'],
  ['BTU/gal',  'British thermal units per gallon.'],
];

function collectReportTerms(samples, seedTerms=[]) {
  const used = new Set(seedTerms);
  const unitTerms = ['mg/L','mg/kg','µg/L','µg/kg','ppm','ppb','%','°C','°F','cSt','BTU/lb','BTU/gal'];
  const textParts = [];

  (samples || []).forEach(s => {
    getReportParams(s).forEach(parameter => {
      const result = s.results?.[parameter];
      const r = result || {};
      const unit = String(Object.prototype.hasOwnProperty.call(r, 'unit')
        ? r.unit
        : (getDefaultUnit(parameter) || '')).trim();
      unitTerms.forEach(term => {
        if (unit.toLowerCase() === term.toLowerCase() ||
            String(parameter).toLowerCase().includes(term.toLowerCase())) used.add(term);
      });
      if (r.qual === '<MDL' || r.stdMdl || r.altMdl) used.add('MDL');
      textParts.push(
        parameter, unit, r.method, r.digestionMethod, r.extractionMethod,
        r.spec, r.qual, r.value, r.overrideValue
      );
    });
  });

  const text = textParts.filter(v => v !== undefined && v !== null).join(' ');
  if (/\bEPA\b/i.test(text)) used.add('EPA');
  if (/\bASTM\b/i.test(text)) used.add('ASTM');
  if (/\bTCLP\b/i.test(text)) used.add('TCLP');
  if (/\bSW-?846\b/i.test(text)) used.add('SW-846');
  if (/(^|[\s,;(])ND([\s,;).]|$)/i.test(text)) used.add('ND');
  if (/\bN\/A\b/i.test(text)) used.add('N/A');

  return REPORT_GLOSSARY.filter(([term]) => used.has(term));
}

function buildAbbreviationsAppendix(samples, seedTerms=[], options={}) {
  const entries = collectReportTerms(samples, seedTerms);
  const pageClass = options.pageClass || 'page';
  const workOrder = options.workOrder || samples?.[0]?.workOrder || '';
  const pageLabel = options.pageNumber && options.totalPages
    ? `Page ${options.pageNumber} of ${options.totalPages}`
    : '';
  const rows = entries.length
    ? entries.map(([term, definition]) => `
        <tr>
          <td style="width:24%;padding:7px 9px;border-bottom:1px solid #ddd;font-weight:700;vertical-align:top;">${term}</td>
          <td style="padding:7px 9px;border-bottom:1px solid #ddd;line-height:1.35;">${definition}</td>
        </tr>`).join('')
    : `<tr><td colspan="2" style="padding:12px;text-align:center;color:#666;">No abbreviations were used in this report.</td></tr>`;

  return `
    <div class="${pageClass} abbreviations-appendix" style="position:relative;min-height:9.2in;">
      <div style="display:flex;justify-content:space-between;font-size:12px;padding-bottom:5px;border-bottom:1px solid #bbb;margin-bottom:20px;">
        <span>Visit our website: www.sterlinganalytical.com</span>
        <span>${workOrder ? `Work Order ${workOrder}` : ''}</span>
      </div>
      <div style="font-size:19px;font-weight:700;margin-bottom:6px;">Abbreviations, Units and Definitions</div>
      <div style="font-size:12px;color:#444;line-height:1.45;margin-bottom:14px;">
        This appendix defines the abbreviations, units and symbols used in this report.
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:12px;border-top:2px solid #222;">
        <thead>
          <tr>
            <th style="padding:6px 9px;text-align:left;border-bottom:2px solid #222;">Term</th>
            <th style="padding:6px 9px;text-align:left;border-bottom:2px solid #222;">Definition</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <div style="position:absolute;bottom:24px;left:0;right:0;font-size:12px;border-top:1px solid #ccc;padding-top:4px;display:flex;justify-content:space-between;">
        <span>Sterling Analytical, Inc.</span>
        <span>${pageLabel}</span>
      </div>
    </div>`;
}


// ── Build HTML report ─────────────────────────────────────────────────────────
function buildReportHTML(samples, opts={}) {
  const LOGO_SRC = LOGO_BASE64;
  if (!samples || samples.length === 0) return '';
  const first = samples[0];
  const client = first.source || first.client || '';
  const contact = first.attention || first.client || '';
  const ids = samples.map(s => s.id);
  const reportNumStr = ids.length > 1 ? `${ids[0]}..${ids[ids.length - 1]}` : String(ids[0] || '');
  const coverChunks = [];
  for (let index = 0; index < samples.length; index += 20) {
    coverChunks.push(samples.slice(index, index + 20));
  }
  const coverPageCount = coverChunks.length;

  const coverPage = coverChunks.map((coverSamples, coverIndex) => {
    const compactCover = coverSamples.length >= 10;
    const renderSampleDescriptions = items => items.map(s =>
      `<div class="sample-description-row">
        <span class="sample-description-id">${s.id}</span>
        <span class="sample-description-text">${s.desc || ''}</span>
      </div>`
    ).join('');
    const columnBreak = Math.ceil(coverSamples.length / 2);
    const sampleDesc = compactCover
      ? `<div class="sample-description-column">${renderSampleDescriptions(coverSamples.slice(0, columnBreak))}</div>
         <div class="sample-description-column">${renderSampleDescriptions(coverSamples.slice(columnBreak))}</div>`
      : renderSampleDescriptions(coverSamples);
    return `
    <div class="page cover-page${compactCover ? ' large-batch' : ''}">
      <div class="header-block">
        <div class="address-block">
          <div class="company-name">Sterling Analytical, Inc.</div>
          <div>15 Agawam Avenue</div>
          <div>West Springfield, MA 01089</div>
          <div>Phone (413) 214-6541&nbsp;&nbsp;Fax (413) 214-6842</div>
          <div>email-customerservice@sterlinganalytical.com</div>
        </div>
        <div class="logo-block">
          <img src="${LOGO_SRC}" alt="Sterling Analytical" style="height:80px;width:auto;"/>
        </div>
      </div>
      <div class="website-line"><strong>Visit our website: www.sterlinganalytical.com</strong></div>
      <div class="report-date-row">
        <strong>Report Date</strong>
        <div class="underline-field" style="min-width:180px;border-bottom:1px solid #333;padding-bottom:2px;margin-left:6px;">
          ${opts.reportDate || ''}
        </div>
        ${opts.poNumber ? `
        <strong style="margin-left:20px;">PO Number</strong>
        <div class="underline-field" style="min-width:140px;border-bottom:1px solid #333;padding-bottom:2px;margin-left:6px;">
          ${opts.poNumber}
        </div>` : ''}
      </div>
      <table class="customer-table">
        <thead><tr><th>Customer</th><th>Contact</th><th>Laboratory Supervisor</th><th>eMail</th></tr></thead>
        <tbody>
          <tr>
            <td>${client}</td><td>${opts.poNumber ? 'PO: '+opts.poNumber : contact}</td>
            <td>Dale Percoski</td>
            <td>customerservice@sterlinganalytical.com</td>
          </tr>
          <tr>
            <td colspan="4" class="sample-desc-cell">
              <div><strong>Sample Description${coverPageCount > 1 ? ` — Cover ${coverIndex + 1} of ${coverPageCount}` : ''}</strong></div>
              <div class="sample-description-list" style="margin-top:4px;">${sampleDesc}</div>
              <div class="sample-desc-spacer"></div>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="samples-analyzed-label"><strong>Samples Analyzed</strong></div>
      <div class="report-numbers-box">
        <strong>Enclosed are Report No(s): ${reportNumStr}</strong>
      </div>
      <div class="thankyou">Thank you for your business</div>
      <div class="signature-block">
        <div class="sig-left">
          <div class="signature-name">Dale Percoski</div>
          <div class="sig-line"></div>
          <div class="sig-title">Dale Percoski, Laboratory Supervisor</div>
        </div>
        <div class="sig-right">
          <div class="sig-date">${today()}</div>
          <div class="sig-line"></div>
          <div class="sig-datelabel"><strong>Date</strong></div>
        </div>
      </div>
      <div class="disclaimer">
        ALL the information contained in this report has been reviewed for accuracy and checked against all quality
        control requirements outlined in each applicable method, including any data obtained from a subcontract laboratory.<br/>
        This report may not be reproduced, except in full, without written approval from Sterling Analytical, Inc.
      </div>
    </div>`;
  }).join('');

  const standardSeedTerms = ['Tech'];
  if (opts.poNumber) standardSeedTerms.push('PO');
  let standardHasRl = false;
  let standardHasDf = false;
  let standardHasWet = false;
  let standardHasQAQC = false;
  samples.forEach(s => getReportResults(s).forEach(result => {
    const r = result || {};
    if (r.reportingLimit !== undefined ? r.reportingLimit : r.rdl) standardHasRl = true;
    if (r.dilutionFactor !== undefined && String(r.dilutionFactor).trim() !== '') standardHasDf = true;
    if (r.reportMode === 'wet' || r.reportMode === 'both') standardHasWet = true;
    if (r.spike || r.dupSpike || r.percentRecovery || r.stdMdl || r.altMdl) standardHasQAQC = true;
  }));
  if (standardHasRl) standardSeedTerms.push('RL');
  if (standardHasDf) standardSeedTerms.push('DF');
  if (standardHasWet) standardSeedTerms.push('AR');
  if (standardHasQAQC) standardSeedTerms.push('QA/QC', 'MDL', 'Dup.', 'Std', 'Alt', '%');
  const compactBatchResults = samples.length >= 10 && samples.every(sample =>
    getReportParams(sample).length <= 3 &&
    !getReportResults(sample).some(result => {
      const r = result || {};
      return r.spike || r.dupSpike || r.percentRecovery || r.stdMdl || r.altMdl;
    })
  );
  const standardTotalPages = compactBatchResults
    ? coverPageCount + Math.ceil(samples.length / 6) + 1
    : coverPageCount + samples.length + 1;

  const resultPageSections = samples.map((s, idx) => {
    const params = getReportParams(s);
    const adminMeta = loadSynced('sl_param_meta', {});

    // Resolve every param's row data ONCE, so we can both check which
    // columns have any data (to decide whether to show them at all) and
    // build the actual row HTML from the same values.
    const rowData = params.map(p => {
      const r = s.results[p] || {};
      const includeSpecification = s.includeSpecificationInReport === true;
      // MDL used for a below-detection result: whichever of Std/Alt MDL is
      // toggled active, falling back to the plain entered value if neither
      // MDL was filled in (keeps old behavior working for samples entered
      // before this feature existed).
      const activeMdl = r.mdlMode === 'alt' ? r.altMdl : r.stdMdl;
      const isLT = r.qual === '<' || r.qual === '<MDL';
      const ltValue = isLT ? (activeMdl || r.value) : null;
      // Override Result takes precedence over the raw entered value
      // wherever it's set — it represents "what actually gets reported,"
      // per the lab's own final QC review, separate from the raw measurement.
      const effectiveValue = r.overrideValue || r.value;
      const displayVal = isLT ? `Less Than ${ltValue}` : (effectiveValue || '');
      // A saved result is the source of truth. Only legacy rows where the
      // unit property never existed may use the parameter fallback.
      const unit = Object.prototype.hasOwnProperty.call(r, 'unit')
        ? r.unit
        : getDefaultUnit(p);
      const dateTested = r.dateTested || '';
      const dryBasis = r.dryBasis || effectiveValue || '';
      const wetBasis = formatAsReceived(r.wetBasis, dryBasis);
      const mode = r.reportMode || 'none';
      const showDry = mode === 'dry' || mode === 'both';
      const showWet = mode === 'wet' || mode === 'both';
      const meta = getParamMeta(p);
      const method = resolveField(r.method, matrixMeta(adminMeta[p], s, 'method'), meta.method);
      const digestionMethod = resolvePreparationField(r.digestionMethod, r.digestionMethodAssigned);
      const extractionMethod = resolvePreparationField(r.extractionMethod, r.extractionMethodAssigned);
      // Acceptance limits are sample-specific and appear only when the
      // technician checks "Include Specification in report" for this sample.
      const spec = includeSpecification && r.spec !== undefined ? r.spec : '';
      const reportingLimit = r.reportingLimit !== undefined ? r.reportingLimit : (r.rdl || '');
      const dilutionFactor = r.dilutionFactor !== undefined ? r.dilutionFactor : '';
      // Evaluate against whichever value is actually the "primary" one
      // being reported for this row (dry basis if shown, else wet, else
      // the plain result) — not against a value the reader can't even see.
      const primaryVal = (mode==='dry'||mode==='both') ? (r.dryBasis||effectiveValue) : (mode==='wet') ? r.wetBasis : effectiveValue;
      // Manual Verdict selection (set in Results Entry) takes priority over
      // the auto-computed spec check — techs can override a parse the auto
      // logic got wrong, or a spec that's too free-form to parse at all.
      const verdict = r.verdict || evaluateSpec(primaryVal, spec);
      return {
        p, displayVal, unit, dateTested, dryBasis, wetBasis, showDry, showWet,
        method, digestionMethod, extractionMethod, spec, verdict, reportingLimit, dilutionFactor,
        // Raw QA/QC fields, carried through for the summary table below —
        // only rendered there if at least one parameter actually has any.
        spike: r.spike, spikeResult: r.spikeResult, dupSpike: r.dupSpike,
        dupSpikeResult: r.dupSpikeResult, percentRecovery: r.percentRecovery,
        stdMdl: r.stdMdl, altMdl: r.altMdl, mdlMode: r.mdlMode,
        overrideValue: r.overrideValue, rawValue: r.value,
      };
    });

    // Determine which result/meta columns appear across all params for
    // THIS sample — a column only renders if at least one parameter
    // actually has a value for it, so e.g. an empty Acceptance Limits column
    // doesn't show up on reports where nothing was specified, while it
    // still shows normally on reports that do have spec values.
    const hasWet    = rowData.some(r => r.showWet);
    const hasMethod = rowData.some(r => r.method);
    const hasSpec   = s.includeSpecificationInReport === true;
    const hasVerdict= rowData.some(r => r.verdict);
    const hasRl     = rowData.some(r => r.reportingLimit);
    const hasDf     = rowData.some(r => r.dilutionFactor);
    const hasQAQC   = rowData.some(r => r.spike || r.dupSpike || r.percentRecovery || r.stdMdl || r.altMdl);
    // Moisture % isn't stored as a separate field — it's simply the entered
    // value of whichever parameter IS the moisture parameter for this
    // sample (same matching EnterResults.jsx uses: name contains both
    // "moisture" and "%"). Wet Basis values for other parameters were
    // calculated from this, so show it alongside them for context.
    // moisturePercent is now persisted directly on whichever result(s) had
    // Wet Basis calculated (see EnterResults.jsx doSaveResults) — this is
    // the correct source for BOTH the quick-moisture toolbar path (which
    // has no dedicated Moisture parameter at all, like this BTU/Lb-only
    // sample) and the dedicated-Moisture-parameter solid-sample path.
    const withMoisture = getReportResults(s).find(r => r?.moisturePercent != null);
    const moistureParamName = params.find(p => p.toLowerCase().includes('moisture') && p.toLowerCase().includes('%'));
    const wetMoisture = withMoisture?.moisturePercent ??
      (moistureParamName ? (s.results[moistureParamName]?.value || s.results[moistureParamName]?.dryBasis) : null);
    const pageNum = idx + coverPageCount + 1;
    const totalPages = standardTotalPages;

    // Single source of truth for the results table's columns — width,
    // header label, and per-row cell content all come from the same list,
    // so the header and the rows are structurally guaranteed to match.
    // (Previously these were built as two separate, hand-maintained
    // template strings that had to stay in sync by hand — a missing CSS
    // width rule or one list falling out of step with the other is exactly
    // what caused columns to visually misalign.)
    const columns = [
      { label: 'Parameter', width: 21, cell: r => r.p },
      ...(hasVerdict ? [{
        label: '', width: 10, align: 'center', noWrap: true,
        cell: r => r.verdict
          ? `<span style="color:${VERDICT_COLOR[r.verdict]||'#333'};font-weight:700;">${VERDICT_LABEL[r.verdict]}</span>`
          : ''
      }] : []),
      {
        label: hasWet ? 'Dry Basis' : 'Results',
        width: 10,
        headerStyle: hasWet ? 'background:#2a5080;color:#fff;' : '',
        cell: r => hasWet ? (r.dryBasis || r.displayVal) : r.displayVal
      },
      ...(hasWet ? [{
        label: 'As Received (AR)', width: 10,
        headerStyle: 'background:#1a7dc4;color:#fff;',
        cell: r => r.showWet ? (r.wetBasis || '') : ''
      }] : []),
      { label: 'Unit', width: 7, cell: r => r.unit },
      ...(hasRl ? [{ label: 'RL', width: 6, align: 'right', noWrap: true, cell: r => r.reportingLimit }] : []),
      ...(hasMethod ? [{ label: 'Method', width: 11, cell: r => r.method }] : []),
      ...(hasDf ? [{ label: 'DF', width: 5, align: 'right', noWrap: true, cell: r => r.dilutionFactor }] : []),
      ...(hasSpec ? [{ label: 'Specification /<br/>Acceptance Limits', width: 17, cell: r => r.spec }] : []),
      { label: 'Date Analyzed', width: 13, noTruncate: true, noWrap: true, cell: r => r.dateTested },
    ];

    const groupedMethodLines = (label, field) => {
      const groups = new Map();
      rowData.forEach(r => {
        const methodValue = String(r[field] || '').trim();
        if (!methodValue || methodValue.toUpperCase() === 'N/A') return;
        if (!groups.has(methodValue)) groups.set(methodValue, []);
        groups.get(methodValue).push(r.p);
      });
      return [...groups.entries()].map(([methodValue, methodParams]) =>
        `<div class="method-line preparation-method-line"><strong>${label}:</strong> ${methodValue} (${methodParams.join(', ')})</div>`
      ).join('');
    };
    const preparationMethodsHtml =
      groupedMethodLines('Digestion Method', 'digestionMethod') +
      groupedMethodLines('Extraction Method', 'extractionMethod');
    const alignStyle = (c) => (c.align === 'right' ? 'text-align:right;' : c.align === 'center' ? 'text-align:center;' : '')
      + (c.noWrap
        ? 'white-space:nowrap;overflow:hidden;text-overflow:clip;'
        : c.noTruncate
          ? 'white-space:normal;overflow:hidden;text-overflow:clip;overflow-wrap:anywhere;'
          : '');
    // Scale cell text to the space assigned to its column instead of
    // replacing important report data with an ellipsis. The estimate is
    // intentionally conservative for Arial; exceptionally long values wrap
    // only after reaching the minimum readable font size.
    const fitCellStyle = (c, content) => {
      if (c.noTruncate || c.noWrap) return '';
      const plainText = String(content ?? '').replace(/<[^>]*>/g, '').trim();
      if (!plainText) return '';
      const usableWidthPx = Math.max(24, (664 * c.width / 100) - 12);
      const estimatedTextWidthPx = plainText.length * 5.7;
      const calculatedSize = 12 * usableWidthPx / Math.max(1, estimatedTextWidthPx);
      const fontSize = Math.min(12, Math.max(8.5, calculatedSize));
      return `font-size:${fontSize.toFixed(2)}px;overflow:hidden;text-overflow:clip;`
        + 'white-space:normal;overflow-wrap:anywhere;word-break:normal;';
    };
    const colgroupHtml = `<colgroup>${columns.map(c => `<col style="width:${c.width}%;">`).join('')}</colgroup>`;
    const theadHtml = `<thead><tr>${
      columns.map(c => `<th style="${alignStyle(c)}${c.headerStyle||''}">${c.label}</th>`).join('')
    }</tr></thead>`;
    const rows = rowData.map(r => `<tr>${
      columns.map(c => {
        const content = c.cell(r);
        return `<td style="${alignStyle(c)}${fitCellStyle(c, content)}">${content}</td>`;
      }).join('')
    }</tr>`).join('');
    return `
      <div class="${compactBatchResults ? 'compact-result-section' : 'page results-page'}">
        <div class="results-top-bar">
          <span>Visit our website: www.sterlinganalytical.com</span>
          <span><strong>Sample Analysis</strong></span>
          <span>Work Order ${s.workOrder || ''}</span>
        </div>
        <table class="sample-info-table"><tbody>
          <tr>
            <td class="si-label">Sample No</td><td class="si-val"><strong>${s.id}</strong></td>
            <td class="si-label">Source</td><td class="si-val">${s.source || ''}</td>
            <td class="si-label">Date Sampled</td><td class="si-val">${s.taken || ''}</td>
            <td class="si-label">Date Received</td><td class="si-val">${s.received || ''}</td>
          </tr>
          <tr>
            <td class="si-label">Source Description</td><td class="si-val" colspan="3">${s.desc || ''}</td>
            <td class="si-label">Tech</td><td class="si-val">${s.tech || ''}</td>
            <td class="si-label">Work Order</td><td class="si-val">${s.workOrder || ''}</td>
          </tr>
        </tbody></table>
        ${preparationMethodsHtml}
        ${hasWet && wetMoisture != null ? `<div class="method-line">Moisture: ${wetMoisture}% (used for As Received calculation)</div>` : ''}
        <table class="results-table">
          ${colgroupHtml}
          ${theadHtml}
          <tbody>${rows || `<tr><td colspan="${columns.length}" style="text-align:center;padding:16px;color:#999;">No results entered</td></tr>`}</tbody>
        </table>
        ${hasQAQC ? `
        <div class="qaqc-title">QA/QC Summary</div>
        <table class="qaqc-table">
          <thead><tr>
            <th>Parameter</th><th>MDL Used</th><th>Spike</th><th>Spike Result</th>
            <th>Dup. Spike</th><th>Dup. Spike Result</th><th>% Recovery</th>
          </tr></thead>
          <tbody>
            ${rowData.filter(r => r.spike || r.dupSpike || r.percentRecovery || r.stdMdl || r.altMdl).map(r => {
              const mdlVal = r.mdlMode === 'alt' ? r.altMdl : r.stdMdl;
              const mdlLabel = mdlVal ? `${mdlVal} (${r.mdlMode === 'alt' ? 'Alt' : 'Std'})` : '';
              return `<tr>
                <td class="param-cell">${r.p}</td>
                <td>${mdlLabel}</td>
                <td>${r.spike||''}</td>
                <td>${r.spikeResult||''}</td>
                <td>${r.dupSpike||''}</td>
                <td>${r.dupSpikeResult||''}</td>
                <td>${r.percentRecovery||''}</td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>` : ''}
        ${(() => {
          const comment = loadSampleComment(s.id);
          const photos = loadSamplePhotos(s.id);
          const hasPhotos = photos.before || photos.after;
          if (!comment && !hasPhotos) return '';
          return `
            ${comment ? `<div class="comment-block"><strong>Comments:</strong> ${comment}</div>` : ''}
            ${hasPhotos ? `
              <div class="photo-section">
                ${photos.before ? `<div class="photo-block"><div class="photo-label">Before</div><img class="report-photo" src="${photos.before}"/></div>` : ''}
                ${photos.after ? `<div class="photo-block"><div class="photo-label">After</div><img class="report-photo" src="${photos.after}"/></div>` : ''}
              </div>` : ''}
          `;
        })()}
        ${compactBatchResults ? '' : `<div class="page-footer">
          <span>Sterling Analytical, Inc.</span>
          <span>Page ${pageNum} of ${totalPages}</span>
        </div>`}
      </div>`;
  }).join('');

  const resultPages = compactBatchResults
    ? `<div class="page compact-results-flow">${resultPageSections}</div>`
    : resultPageSections;

  const appendixPage = buildAbbreviationsAppendix(samples, standardSeedTerms, {
    pageClass: 'page',
    workOrder: first.workOrder || '',
    pageNumber: standardTotalPages,
    totalPages: standardTotalPages,
  });

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Sterling Analytical Report</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:Arial,sans-serif;font-size:13px;color:#111;background:white;}
    .page{width:760px;margin:0 auto;padding:36px 48px;min-height:1020px;position:relative;}
    .header-block{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px;}
    .address-block{font-size:13px;line-height:1.7;}
    .company-name{font-size:17px;font-weight:bold;margin-bottom:2px;}
    .logo-block{text-align:right;}
    .website-line{font-size:13px;margin:4px 0 16px 0;}
    .report-date-row{display:flex;align-items:flex-end;gap:14px;margin-bottom:12px;font-weight:bold;font-size:13px;}
    .underline-field{flex:1;border-bottom:1px solid #111;max-width:260px;}
    .customer-table{width:100%;border-collapse:collapse;font-size:13px;margin-bottom:16px;}
    .customer-table th{border:1px solid #111;padding:5px 8px;font-weight:bold;text-align:left;}
    .customer-table td{border:1px solid #111;padding:5px 8px;vertical-align:top;}
    .sample-desc-cell{padding:6px 8px!important;}
    .sample-description-list>div,.sample-description-column>div{font-size:12px;line-height:1.2;}
    .sample-description-row{display:grid;grid-template-columns:58px minmax(0,1fr);column-gap:4px;align-items:start;margin-bottom:2px!important;break-inside:avoid;page-break-inside:avoid;}
    .sample-description-id{font-family:monospace;color:#555;font-size:12px;white-space:nowrap;}
    .sample-description-text{font-size:12px;line-height:1.2;white-space:normal;overflow-wrap:anywhere;word-break:normal;}
    .sample-desc-spacer{height:24px;}
    .cover-page.large-batch .sample-description-list{display:grid;grid-template-columns:1fr 1fr;column-gap:18px;row-gap:0;}
    .cover-page.large-batch .sample-description-column>.sample-description-row{margin-bottom:2px!important;line-height:1.2;font-size:12px;}
    .cover-page.large-batch .sample-desc-spacer{height:4px;}
    .cover-page.large-batch .customer-table{margin-bottom:8px;}
    .cover-page.large-batch .report-numbers-box{min-height:62px;padding:12px 16px;margin-bottom:10px;font-size:15px;}
    .cover-page.large-batch .thankyou{font-size:23px;margin:10px 0 14px;}
    .cover-page.large-batch .signature-block{margin-bottom:10px;}
    .cover-page.large-batch .disclaimer{font-size:10.5px;line-height:1.3;margin-top:4px;}
    .samples-analyzed-label{font-size:13px;font-weight:bold;margin-bottom:5px;}
    .report-numbers-box{border:1px solid #111;padding:16px 20px;min-height:300px;font-size:17px;font-weight:bold;margin-bottom:24px;}
    .thankyou{font-size:30px;font-weight:bold;margin:24px 0 32px 0;}
    .signature-block{display:flex;gap:60px;margin-bottom:24px;align-items:flex-end;}
    .sig-left{flex:1;} .sig-right{width:180px;}
    .signature-name{font-family:'Brush Script MT','Comic Sans MS',cursive;font-size:24px;color:#111;}
    .sig-line{border-bottom:1px solid #111;margin:2px 0;}
    .sig-title{font-style:italic;font-size:12px;}
    .sig-date{font-size:16px;font-weight:bold;}
    .sig-datelabel{font-size:13px;font-weight:bold;}
    .disclaimer{font-size:12px;line-height:1.6;color:#222;margin-top:8px;}
    .results-top-bar{display:flex;justify-content:space-between;font-size:12px;padding-bottom:4px;border-bottom:1px solid #bbb;margin-bottom:8px;}
    .sample-info-table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:6px;}
    .sample-info-table td{border:1px solid #999;padding:3px 6px;}
    .si-label{background:#f0f0f0;font-weight:bold;white-space:nowrap;width:100px;}
    .si-val{min-width:90px;}
    .method-line{font-size:11.5px;color:#333;margin:2px 0;line-height:1.25;}
    .preparation-method-line{font-size:12px;color:#222;}
    .method-line:last-of-type{margin-bottom:6px;}
    .results-table{width:100%;border-collapse:collapse;font-size:11.5px;margin:5px 0 12px;
      table-layout:fixed;border-top:1.5px solid #222;}
    .results-table thead tr{border-bottom:2px solid #222;background:#fff;}
    .results-table th{padding:3px 5px 4px;text-align:left;font-weight:700;color:#111;
      white-space:normal;word-break:normal;overflow-wrap:anywhere;line-height:1.1;vertical-align:bottom;}
    .results-table td{padding:3px 5px;border-bottom:1px solid #e5e5e5;line-height:1.15;
      overflow:hidden;text-overflow:clip;white-space:normal;overflow-wrap:anywhere;
      word-break:normal;vertical-align:middle;}
    .results-table tbody tr:last-child td{border-bottom:1.5px solid #222;}
    .results-table tr:nth-child(even) td{background:#fcfcfc;}
    .qaqc-title{font-size:12px;font-weight:bold;color:#9c27b0;margin:14px 0 4px;}
    .qaqc-table{width:100%;border-collapse:collapse;font-size:11px;margin-bottom:8px;}
    .qaqc-table th{background:#faf5ff;border:1px solid #d8c0e8;padding:4px 6px;text-align:left;font-weight:bold;color:#6a1b9a;}
    .qaqc-table td{border:1px solid #e5d5f0;padding:3px 6px;}
    .comment-block{margin-top:14px;padding:8px 10px;background:#f8f8f8;border:1px solid #ddd;border-radius:4px;font-size:12px;line-height:1.5;}
    .photo-section{display:flex;gap:14px;margin-top:14px;}
    .photo-block{flex:1;text-align:center;}
    .photo-label{font-size:11px;font-weight:bold;color:#444;margin-bottom:4px;}
    .report-photo{max-width:100%;max-height:260px;border:1px solid #ccc;border-radius:4px;object-fit:contain;}
    .page-footer{position:absolute;bottom:24px;left:48px;right:48px;font-size:12px;color:#000;border-top:1px solid #ccc;padding-top:4px;display:flex;justify-content:space-between;}
    .compact-results-flow{padding-top:24px;padding-bottom:24px;}
    .compact-result-section{position:relative;margin-bottom:16px;padding-bottom:5px;break-inside:avoid;page-break-inside:avoid;}
    .compact-result-section+.compact-result-section{border-top:1px solid #b9c0ca;padding-top:12px;}
    @media print{
      body{margin:0;}
      /* min-height was previously reset to "auto" here, which collapsed
         the page container down to just its content height — since the
         footer is absolutely positioned to the bottom of THIS container,
         it ended up sitting right after the table instead of the actual
         bottom of the printed page. Letter-size page height (11in) minus
         the container's own top/bottom padding keeps the footer pinned to
         the real page bottom regardless of how much content is above it. */
      .page{width:100%;margin:0;padding:20px 28px;page-break-after:always;min-height:9.2in;}
      .page:last-child{page-break-after:auto;}
      .cover-page.large-batch{min-height:9.2in;page-break-inside:avoid;break-inside:avoid-page;}
      .compact-results-flow{min-height:auto;padding:14px 28px 20px;}
      .compact-result-section{break-inside:avoid-page;page-break-inside:avoid;margin-bottom:12px;}
    }
  </style></head><body>${coverPage}${resultPages}${appendixPage}</body></html>`;
}

// ── D975 report format — compact, one self-contained page per sample, no
// separate cover page. Units are embedded in parameter names themselves
// (e.g. "Flash Point, °F") rather than a separate Unit column, matching
// how this format has always been laid out. ────────────────────────────────
function buildD975ReportHTML(samples, opts={}) {
  if (!samples || samples.length === 0) return '';

  const pages = samples.map(s => {
    const params = getReportParams(s);
    const adminMeta = loadSynced('sl_param_meta', {});
    const failedParams = [];
    const includeSpecification = s.includeSpecificationInReport === true;

    const rowResults = params.map(p => {
      const r = s.results[p] || {};
      const isLT = r.qual === '<' || r.qual === '<MDL';
      const effectiveValue = r.overrideValue || r.value; // Override Result takes precedence, same as the standard format
      const activeMdl = r.mdlMode === 'alt' ? r.altMdl : r.stdMdl;
      const meta = getParamMeta(p);
      const method = resolveField(r.method, matrixMeta(adminMeta[p], s, 'method'), meta.method);
      const digestionMethod = resolvePreparationField(r.digestionMethod, r.digestionMethodAssigned);
      const spec = includeSpecification && r.spec !== undefined ? r.spec : '';
      const primaryVal = r.dryBasis || effectiveValue;
      // Manual Verdict selection takes priority over the auto spec check.
      const verdict = isLT ? null : (r.verdict || evaluateSpec(primaryVal, spec));
      const isFail = verdict === 'less' || verdict === 'more' || verdict === 'fail';
      if (isFail) failedParams.push(p);

      let displayVal;
      if (isLT) {
        displayVal = `Less Than ${activeMdl || effectiveValue}`;
      } else if (isFail) {
        displayVal = `<span class="d975-fails">Fails</span> ${effectiveValue || ''}`;
      } else {
        displayVal = effectiveValue || '';
      }

      return { p, displayVal, spec, method, digestionMethod };
    });

    // Digestion Method only gets its own column if at least one parameter
    // on this sample actually has one — same "don't show an empty column"
    // rule as every other optional column in this file.
    const hasDigestionMethod = rowResults.some(r => r.digestionMethod);
    const d975Columns = [
      { label: 'Parameter', cell: r => r.p },
      { label: 'Results', cell: r => r.displayVal },
      ...(includeSpecification ? [{ label: 'Acceptance Limits', cell: r => r.spec }] : []),
      { label: 'ASTM Methods', cell: r => r.method },
      ...(hasDigestionMethod ? [{ label: 'Digestion Method', cell: r => r.digestionMethod }] : []),
    ];
    const rows = rowResults.map(r => `<tr>${
      d975Columns.map(c => `<td>${c.cell(r)}</td>`).join('')
    }</tr>`).join('');

    // A certification line always appears on this format — if no comment
    // was manually saved for this sample, auto-generate the pass/fail
    // statement instead of leaving the report without one.
    const savedComment = loadSampleComment(s.id);
    const autoComment = failedParams.length > 0
      ? `The sample fails in ${failedParams.join(', ')} parameter${failedParams.length>1?'s':''}.`
      : `The sample meets the standards as specified under ASTM-D975.`;
    const comment = savedComment || autoComment;
    const commentIsFail = !savedComment && failedParams.length > 0;
    const photos = loadSamplePhotos(s.id);
    const hasPhotos = photos.before || photos.after;

    return `
      <div class="d975-page">
        <div class="d975-header">
          <div class="d975-left">
            <div><strong>Source:</strong> ${s.source || s.client || ''}</div>
            <div><strong>Contact:</strong> ${s.attention || ''}</div>
            <div><strong>Sample:</strong> ${s.source || s.client || ''}</div>
            <div><strong>Description:</strong> ${s.desc || ''}</div>
            <div><strong>Sample No:</strong> ${s.id}</div>
            <div><strong>Source ID:</strong> <span class="d975-sourceid">${s.sourceId || ''}</span></div>
          </div>
          <div class="d975-right">
            <div><strong>Work Order:</strong> ${s.workOrder || ''}</div>
            <div><strong>Date Completed:</strong> ${s.dateCompleted || ''}</div>
            <div><strong>Date Received:</strong> ${s.received || ''}</div>
            <div><strong>Date Taken:</strong> ${s.taken || ''}</div>
          </div>
        </div>
        <table class="d975-table">
          <thead><tr>${d975Columns.map(c => `<th>${c.label}</th>`).join('')}</tr></thead>
          <tbody>${rows || `<tr><td colspan="${d975Columns.length}" style="text-align:center;color:#999;padding:10px;">No results entered</td></tr>`}</tbody>
        </table>
        <div class="d975-comment" style="${commentIsFail ? 'color:#c00;' : ''}">${comment}</div>
        ${hasPhotos ? `
          <div class="photo-section">
            ${photos.before ? `<div class="photo-block"><div class="photo-label">Before</div><img class="report-photo" src="${photos.before}"/></div>` : ''}
            ${photos.after ? `<div class="photo-block"><div class="photo-label">After</div><img class="report-photo" src="${photos.after}"/></div>` : ''}
          </div>` : ''}
        <div class="d975-footer">
          <span>Email: customerservice@sterlinganalytical.com</span>
          <span class="d975-sig">Dale Percoski, Laboratory Supervisor</span>
        </div>
      </div>`;
  }).join('');

  const d975Appendix = buildAbbreviationsAppendix(samples, ['ASTM', 'ASTM D975'], {
    pageClass: 'd975-page',
    workOrder: samples[0]?.workOrder || '',
    pageNumber: samples.length + 1,
    totalPages: samples.length + 1,
  });

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Sterling Analytical Report — D975</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:Arial,sans-serif;font-size:13px;color:#111;background:white;}
    .d975-page{width:700px;margin:0 auto;padding:28px 36px;min-height:960px;position:relative;}
    .d975-header{display:flex;justify-content:space-between;margin-bottom:16px;font-size:13px;line-height:1.9;}
    .d975-left{flex:1;}
    .d975-right{width:230px;text-align:right;}
    .d975-sourceid{border:1px solid #c00;padding:1px 5px;color:#c00;font-weight:600;}
    .d975-table{width:100%;border-collapse:collapse;font-size:13px;margin-bottom:16px;}
    .d975-table th{border-bottom:2px solid #111;text-align:left;padding:5px 6px;color:#1a4a80;}
    .d975-table td{padding:4px 6px;border-bottom:1px solid #eee;}
    .d975-param,.d975-method{color:#1a4a80;}
    .d975-comment{margin:20px 0 0;padding-top:10px;border-top:1px solid #ccc;font-weight:600;color:#1a6b42;font-size:13px;}
    .d975-fails{color:#c00;font-weight:bold;}
    .photo-section{display:flex;gap:14px;margin-top:14px;}
    .photo-block{flex:1;text-align:center;}
    .photo-label{font-size:11px;font-weight:bold;color:#444;margin-bottom:4px;}
    .report-photo{max-width:100%;max-height:220px;border:1px solid #ccc;border-radius:4px;object-fit:contain;}
    .d975-footer{position:absolute;bottom:24px;left:36px;right:36px;display:flex;justify-content:space-between;
      font-size:12px;border-top:1px solid #333;padding-top:8px;}
    .d975-sig{font-style:italic;}
    @media print{
      body{margin:0;}
      .d975-page{width:100%;margin:0;padding:20px 24px;page-break-after:always;min-height:9.2in;}
      .d975-page:last-child{page-break-after:auto;}
    }
  </style></head><body>${pages}${d975Appendix}</body></html>`;
}

// ── ICP / Metals report format — Parameter | Results | RDL | Method |
// Analyzed/Time | Tech | Results(final). All samples share one page (this
// format is normally used for multi-element panels run together), grouped
// by sample with its own mini-header. The "Tech" column in the legacy
// version isn't actually a person's initials — it's a qualifier-code
// fallback ('dfp') already used elsewhere in this codebase (see the Excel
// export), so it's replicated exactly rather than guessing a real name. ───
function buildICPReportHTML(samples, opts={}) {
  if (!samples || samples.length === 0) return '';
  const adminMeta = loadSynced('sl_param_meta', {});

  const sections = samples.map(s => {
    const params = getReportParams(s);
    const rowResults = params.map(p => {
      const r = s.results[p] || {};
      const isLT = r.qual === '<' || r.qual === '<MDL';
      const effectiveValue = r.overrideValue || r.value;
      const activeMdl = r.mdlMode === 'alt' ? r.altMdl : r.stdMdl;
      const unit = Object.prototype.hasOwnProperty.call(r, 'unit')
        ? r.unit
        : getDefaultUnit(p);
      const meta = getParamMeta(p);
      const method = resolveField(r.method, matrixMeta(adminMeta[p], s, 'method'), meta.method);
      const digestionMethod = resolvePreparationField(r.digestionMethod, r.digestionMethodAssigned);
      const rdl = r.rdl || '';
      const dateTime = [r.dateTested, r.timeTested].filter(Boolean).join(' ');
      const techCode = r.qual || 'dfp';
      const finalResult = r.overrideValue || ''; // second "Results" column — only shown when an override exists
      return { p, isLT, effectiveValue, unit, method, digestionMethod, rdl, dateTime, techCode, finalResult };
    });

    // Digestion Method only gets its own column if at least one parameter
    // on this sample actually has one.
    const hasDigestionMethod = rowResults.some(r => r.digestionMethod);
    const icpColumns = [
      { cls: 'icp-param',    label: 'Parameter',      cell: r => r.p },
      { cls: 'icp-result',   label: 'Results',        cell: r => `${r.isLT ? 'Less Than ' : ''}${r.effectiveValue||''} ${r.unit}` },
      { cls: 'icp-rdl',      label: 'RDL',             cell: r => r.rdl },
      { cls: 'icp-method',   label: 'Method',          cell: r => r.method },
      ...(hasDigestionMethod ? [{ cls: 'icp-digestion', label: 'Digestion Method', cell: r => r.digestionMethod }] : []),
      { cls: 'icp-datetime', label: 'Analyzed/Time',   cell: r => r.dateTime },
      { cls: 'icp-tech',     label: 'Tech',            cell: r => r.techCode },
      { cls: 'icp-final',    label: 'Results',         cell: r => r.finalResult },
    ];
    const rows = rowResults.map(r => `<tr>${
      icpColumns.map(c => `<td class="${c.cls}">${c.cell(r)}</td>`).join('')
    }</tr>`).join('');

    return `
      <div class="icp-section">
        <div class="icp-header">
          <div class="icp-desc"><strong>Sample Description</strong><br/>${s.id}&nbsp;&nbsp;${s.desc||''}</div>
          <div class="icp-source"><strong>Source</strong><br/>${s.source||s.client||''}</div>
          <div class="icp-taken"><strong>Taken/Time</strong><br/>${s.taken||''}${s.timeReceived?' '+s.timeReceived:''}</div>
          <div class="icp-received"><strong>Received</strong><br/>${s.received||''}</div>
        </div>
        <table class="icp-table">
          <thead><tr>${icpColumns.map(c => `<th class="${c.cls}">${c.label}</th>`).join('')}</tr></thead>
          <tbody>${rows || `<tr><td colspan="${icpColumns.length}" style="text-align:center;color:#999;padding:10px;">No results entered</td></tr>`}</tbody>
        </table>
      </div>`;
  }).join('<div class="icp-divider"></div>');

  const icpAppendix = buildAbbreviationsAppendix(samples, ['ICP', 'RDL', 'Tech'], {
    pageClass: 'icp-appendix-page',
    workOrder: samples[0]?.workOrder || '',
  });

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Sterling Analytical Report — ICP</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:Arial,sans-serif;font-size:12px;color:#111;background:white;padding:24px;}
    .icp-section{margin-bottom:18px;}
    .icp-header{display:flex;gap:24px;margin-bottom:8px;font-size:11px;border-bottom:1px solid #333;padding-bottom:6px;}
    .icp-header > div{flex:1;}
    .icp-header strong{font-size:10px;text-transform:uppercase;color:#555;}
    .icp-table{width:100%;border-collapse:collapse;font-size:11px;table-layout:fixed;}
    .icp-table th{border-bottom:1px solid #111;text-align:left;padding:4px 6px;color:#1a4a80;font-size:10px;}
    .icp-table td{padding:3px 6px;border-bottom:1px solid #f0f0f0;}
    .icp-param{color:#1a4a80;width:20%;}
    .icp-result{width:16%;}
    .icp-rdl{width:9%;}
    .icp-method{width:16%;}
    .icp-digestion{width:14%;}
    .icp-datetime{width:14%;}
    .icp-tech{width:8%;}
    .icp-final{width:9%;color:#1a6b42;font-weight:600;}
    .icp-divider{border-top:2px solid #ccc;margin:14px 0;}
    .icp-appendix-page{width:100%;padding:20px 28px;page-break-before:always;}
    @media print{ body{padding:0 20px;} .icp-section{page-break-inside:avoid;} .icp-appendix-page{page-break-before:always;} }
  </style></head><body>${sections}${icpAppendix}</body></html>`;
}

// ── Proximate/Ultimate Analysis format (solid fuels / biomass) — Parameter |
// Date Analyzed | As Received | Dry | Method. "As Received" and "Dry" map
// directly onto the existing wetBasis/dryBasis fields (same underlying
// concept, just relabeled to match this format's terminology), so no new
// data model fields are needed for this one. ───────────────────────────────
function buildBiomassReportHTML(samples, opts={}) {
  if (!samples || samples.length === 0) return '';
  const adminMeta = loadSynced('sl_param_meta', {});

  const pages = samples.map(s => {
    const params = getReportParams(s);
    const rowResults = params.map(p => {
      const r = s.results[p] || {};
      const isLT = r.qual === '<' || r.qual === '<MDL';
      const effectiveValue = r.overrideValue || r.value;
      const dry = r.dryBasis || effectiveValue || '';
      const asReceived = formatAsReceived(r.wetBasis, dry);
      const meta = getParamMeta(p);
      const method = resolveField(r.method, matrixMeta(adminMeta[p], s, 'method'), meta.method);
      const digestionMethod = resolvePreparationField(r.digestionMethod, r.digestionMethodAssigned);
      const dryDisplay = isLT ? `Less Than ${dry}` : dry;
      return { p, dateTested: r.dateTested||'', asReceived, dryDisplay, method, digestionMethod };
    });

    const hasDigestionMethod = rowResults.some(r => r.digestionMethod);
    const proxColumns = [
      { cls: 'prox-param',  label: 'Parameter',        cell: r => r.p },
      { cls: 'prox-date',   label: 'Date Analyzed',    cell: r => r.dateTested },
      { cls: 'prox-ar',     label: 'As Received',      cell: r => r.asReceived },
      { cls: 'prox-dry',    label: 'Dry',               cell: r => r.dryDisplay },
      { cls: 'prox-method', label: 'Method',            cell: r => r.method },
      ...(hasDigestionMethod ? [{ cls: 'prox-digestion', label: 'Digestion Method', cell: r => r.digestionMethod }] : []),
    ];
    const rows = rowResults.map(r => `<tr>${
      proxColumns.map(c => `<td class="${c.cls}">${c.cell(r)}</td>`).join('')
    }</tr>`).join('');

    const comment = loadSampleComment(s.id);

    return `
      <div class="prox-page">
        <div class="prox-header">
          <div class="prox-left">
            <div><strong>Sample Number</strong> ${s.id}</div>
            <div><strong>Station</strong> ${s.source||s.client||''}</div>
            <div><strong>Contact</strong> ${s.attention||''}</div>
            <div><strong>Date Received</strong> ${s.received||''}</div>
            <div><strong>Date Taken</strong> ${s.taken||''}</div>
          </div>
          <div class="prox-right">
            <div><strong>Report Date</strong> ${opts.reportDate||''}</div>
            <div><strong>Work Order</strong> ${s.workOrder||''}</div>
            <div><strong>Source Identification</strong><br/>${s.sourceId||s.desc||''}</div>
          </div>
        </div>
        <div class="prox-title">Proximate/Ultimate Analysis</div>
        <table class="prox-table">
          <thead><tr>${proxColumns.map(c => `<th>${c.label}</th>`).join('')}</tr></thead>
          <tbody>${rows || `<tr><td colspan="${proxColumns.length}" style="text-align:center;color:#999;padding:10px;">No results entered</td></tr>`}</tbody>
        </table>
        <div class="prox-comments-label">Comments</div>
        <div class="prox-comments-box">${comment}</div>
      </div>`;
  }).join('');

  const biomassAppendix = buildAbbreviationsAppendix(samples, [], {
    pageClass: 'prox-page',
    workOrder: samples[0]?.workOrder || '',
    pageNumber: samples.length + 1,
    totalPages: samples.length + 1,
  });

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Sterling Analytical Report — Proximate/Ultimate</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:Arial,sans-serif;font-size:12px;color:#111;background:white;}
    .prox-page{width:700px;margin:0 auto;padding:24px 32px;min-height:900px;position:relative;}
    .prox-header{display:flex;justify-content:space-between;margin-bottom:12px;font-size:12px;line-height:1.8;}
    .prox-left{flex:1;} .prox-right{width:260px;text-align:right;}
    .prox-title{background:#dde4f5;text-align:center;font-weight:bold;padding:5px;margin-bottom:6px;font-size:13px;}
    .prox-table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:16px;}
    .prox-table th{border-bottom:2px solid #111;text-align:left;padding:5px 6px;background:#eef1fa;}
    .prox-table td{padding:4px 6px;border-bottom:1px solid #eee;}
    .prox-param{color:#1a4a80;}
    .prox-comments-label{font-weight:bold;margin-bottom:4px;}
    .prox-comments-box{border:1px solid #999;min-height:60px;padding:8px;border-radius:3px;}
    @media print{ .prox-page{width:100%;min-height:auto;page-break-after:always;} .prox-page:last-child{page-break-after:auto;} }
  </style></head><body>${pages}${biomassAppendix}</body></html>`;
}

// ── Report format registry — add more formats (ICP, Biomass, etc.) here
// the same way, each as its own build function taking (samples, opts). ────
const sortedReportBuilder = builder => (samples, opts) =>
  builder(sortSamplesById(samples), opts);

const REPORT_FORMATS = {
  standard: { label: 'Standard (multi-page, full detail)', build: sortedReportBuilder(buildReportHTML) },
  d975:     { label: 'D975 — Fuel Oil (compact, one page per sample)', build: sortedReportBuilder(buildD975ReportHTML) },
  icp:      { label: 'ICP / Metals (RDL, Analyzed/Time, Tech)', build: sortedReportBuilder(buildICPReportHTML) },
  biomass:  { label: 'Proximate/Ultimate — Biomass (As Received / Dry)', build: sortedReportBuilder(buildBiomassReportHTML) },
};

// ── Excel export ──────────────────────────────────────────────────────────────
async function exportToExcel(workOrderGroups, toast) {
  if (!window.XLSX) {
    toast('info', 'Loading Excel library...');
    await new Promise((res, rej) => {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js';
      s.onload = res; s.onerror = rej;
      document.head.appendChild(s);
    });
  }
  const XLSX = window.XLSX;
  const wb = XLSX.utils.book_new();
  const allSamples = workOrderGroups.flatMap(g => g.samples);

  const summaryData = [
    ['Sterling Analytical, Inc. — Laboratory Report'],
    [`Generated: ${new Date().toLocaleString()}`],
    [],
    ['Work Order','Sample #','Source / Client','Description','Test Type','Date Received','Date Completed','Technician','Price ($)','Billed'],
    ...allSamples.map(s => [s.workOrder,s.id,s.source,s.desc,s.testType,s.received,s.dateCompleted,s.tech,s.price||0,s.billed?'Yes':'No']),
  ];
  const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
  wsSummary['!cols'] = [12,10,22,28,12,13,13,8,10,8].map(w=>({wch:w}));
  XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

  workOrderGroups.forEach(group => {
    const rows = [
      ['Sterling Analytical, Inc.'],
      [`Work Order: ${group.workOrder}`],
      [`Client: ${group.samples[0]?.source||group.samples[0]?.client||''}`],
      [`Date: ${today()}`],
      [],
    ];
    group.samples.forEach(s => {
      rows.push([`Sample ${s.id} — ${s.desc||s.source}`]);
      rows.push(['Parameter','Result','Unit','Qualifier','Date Analyzed','Method']);
      getReportParams(s).forEach(p=>{
        const r = s.results?.[p] || {};
        const isLT = r.qual==='<'||r.qual==='<MDL';
        rows.push([p, isLT?`< ${r.value}`:(r.value||'ND'),
          Object.prototype.hasOwnProperty.call(r, 'unit') ? r.unit : getDefaultUnit(p),
          r.qual||'dfp', r.dateTested||'', 'EPA']);
      });
      if (getReportParams(s).length===0) rows.push(['No reportable results entered','','','','','']);
      rows.push([]);
    });
    rows.push(['This report may not be reproduced except in full without written approval from Sterling Analytical, Inc.']);
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [36,14,10,10,13,16].map(w=>({wch:w}));
    XLSX.utils.book_append_sheet(wb, ws, `WO ${group.workOrder}`.slice(0,31));
  });

  XLSX.writeFile(wb, `Sterling_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
  toast('success', `Excel exported — ${workOrderGroups.length} work order(s), ${allSamples.length} sample(s)`);
}

// ── Main Component ────────────────────────────────────────────────────────────
// ── Sample Notes Modal — comments (with reusable library) + before/after photos ─
function SampleNotesModal({ sample, onClose, toast }) {
  const [comment, setComment] = useState(() => loadSampleComment(sample.id));
  const [library, setLibrary] = useState(() => loadCommentLibrary());
  const [photos, setPhotos] = useState(() => loadSamplePhotos(sample.id));
  const [saveToLibrary, setSaveToLibrary] = useState(false);

  const handlePhotoUpload = (which, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhotos(p => ({ ...p, [which]: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    saveSampleComment(sample.id, comment);
    saveSamplePhotos(sample.id, photos);
    if (saveToLibrary && comment && !library.includes(comment)) {
      const next = [...library, comment];
      saveCommentLibrary(next);
    }
    toast('success', `Notes saved for Sample ${sample.id}`);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{width:620}} onClick={e=>e.stopPropagation()}>
        <div className="modal-header">
          <h2>📝 Notes — Sample {sample.id}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group" style={{marginBottom:10}}>
            <label>Comment</label>
            <textarea value={comment} onChange={e=>setComment(e.target.value)} rows={4}
              placeholder="e.g. Sample received in good condition, no visible damage..."
              style={{width:'100%',border:'1px solid #cdd3e4',borderRadius:4,padding:'6px 8px',
                fontSize:12,resize:'vertical',fontFamily:'inherit',outline:'none'}}/>
            <label style={{display:'flex',alignItems:'center',gap:6,marginTop:6,fontSize:11,cursor:'pointer'}}>
              <input type="checkbox" checked={saveToLibrary}
                onChange={e=>setSaveToLibrary(e.target.checked)}/>
              Save this comment for reuse on future reports
            </label>
          </div>

          {library.length > 0 && (
            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,fontWeight:600,color:'#666',marginBottom:4}}>
                Previously saved comments — click to use:
              </div>
              <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                {library.map((c,i)=>(
                  <button key={i} onClick={()=>setComment(c)}
                    style={{fontSize:10,padding:'3px 8px',borderRadius:12,border:'1px solid #cdd3e4',
                      background:'#f0f4ff',cursor:'pointer',color:'#1e3060'}}>
                    {c.length>44 ? c.slice(0,44)+'…' : c}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div style={{display:'flex',gap:16}}>
            {['before','after'].map(which=>(
              <div key={which} style={{flex:1}}>
                <label style={{fontSize:11,fontWeight:600,textTransform:'capitalize'}}>{which} Photo</label>
                <div style={{border:'2px dashed #cdd3e4',borderRadius:6,padding:10,
                  textAlign:'center',marginTop:4,minHeight:100,display:'flex',
                  alignItems:'center',justifyContent:'center'}}>
                  {photos[which] ? (
                    <div>
                      <img src={photos[which]} alt={which}
                        style={{maxWidth:'100%',maxHeight:120,borderRadius:4}}/>
                      <button onClick={()=>setPhotos(p=>({...p,[which]:null}))}
                        style={{display:'block',margin:'6px auto 0',fontSize:10,color:'#dc2626',
                          background:'none',border:'none',cursor:'pointer'}}>
                        Remove
                      </button>
                    </div>
                  ) : (
                    <label style={{cursor:'pointer',fontSize:11,color:'#1e3060'}}>
                      📷 Upload
                      <input type="file" accept="image/*" style={{display:'none'}}
                        onChange={e=>handlePhotoUpload(which, e.target.files[0])}/>
                    </label>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={handleSave}>💾 Save Notes</button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function Reports({ toast, isActive = false }) {
  const [refreshTick, setRefreshTick] = useState(0);

  // Re-render every 2 seconds to pick up new results
  useEffect(() => {
    const interval = setInterval(() => setRefreshTick(t => t + 1), 2000);
    return () => clearInterval(interval);
  }, []);

  // Always read fresh from localStorage on every tick
  const allSamples = useMemo(() => db.getSamples(), [refreshTick]);
  const [search, setSearch] = useState({ id:'', workOrder:'', source:'', desc:'' });
  const [selectedWOs, setSelectedWOs] = useState(new Set());
  const [expandedWOs, setExpandedWOs] = useState(new Set());
  const [exporting,    setExporting]    = useState(false);
  const [printModal,   setPrintModal]   = useState(null); // {groups} when open
  const [printDate,    setPrintDate]    = useState(() => new Date().toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'}));
  const [printPO,      setPrintPO]      = useState('');
  const [viewMode, setViewMode] = useState('completed');
  const [reportFormat, setReportFormat] = useState('standard');
  const [emailModal, setEmailModal] = useState(null);
  const [notesModal, setNotesModal] = useState(null); // sample object when open
  const [serverStatus, setServerStatus] = useState(null); // null | 'ok' | 'error'
  const [sending, setSending] = useState(false);

  // Keep an in-progress report workflow mounted when another user saves data.
  // Without this lock, the normal five-second background refresh can clear
  // filters, checked work orders, print settings, notes, or a composed email.
  // An idle Reports screen remains live and continues to refresh normally.
  const reportWorkflowActive = isActive && (
    selectedWOs.size > 0 ||
    expandedWOs.size > 0 ||
    Boolean(search.id || search.workOrder || search.source || search.desc) ||
    viewMode !== 'completed' ||
    reportFormat !== 'standard' ||
    Boolean(printModal || emailModal || notesModal) ||
    exporting || sending
  );

  useEffect(() => {
    const owner = 'reports-workflow';
    if (reportWorkflowActive) beginUiEdit(owner);
    else endUiEdit(owner);
    return () => endUiEdit(owner);
  }, [reportWorkflowActive]);

  // Check if email server is running
  React.useEffect(() => {
    fetch(`${MAPI_URL}/health`)
      .then(r => r.json())
      .then(d => setServerStatus(d.configured ? 'ready' : 'unconfigured'))
      .catch(() => setServerStatus('offline'));
  }, []);

  const workOrderGroups = useMemo(() => {
    let samples = allSamples;
    if (viewMode==='completed') samples = samples.filter(s=>s.completed);
    if (viewMode==='pending')   samples = samples.filter(s=>!s.completed);
    if (search.id)        samples = samples.filter(s=>String(s.id).includes(search.id.trim()));
    if (search.workOrder) samples = samples.filter(s=>s.workOrder?.toLowerCase().includes(search.workOrder.toLowerCase().trim()));
    if (search.source)    samples = samples.filter(s=>(s.source?.toLowerCase()||'').includes(search.source.toLowerCase().trim())||(s.client?.toLowerCase()||'').includes(search.source.toLowerCase().trim()));
    if (search.desc)      samples = samples.filter(s=>s.desc?.toLowerCase().includes(search.desc.toLowerCase().trim()));
    const map = new Map();
    samples.forEach(s => {
      const wo = s.workOrder||'NO-WO';
      if (!map.has(wo)) map.set(wo,{workOrder:wo,samples:[],allComplete:true});
      const g = map.get(wo);
      g.samples.push(s);
      if (!s.completed) g.allComplete = false;
    });
    const groups = Array.from(map.values());
    groups.forEach(group => group.samples.sort(compareSamplesById));
    return groups.sort((a,b)=>Math.max(...b.samples.map(s=>s.id))-Math.max(...a.samples.map(s=>s.id)));
  }, [allSamples, search, viewMode, refreshTick]);

  const toggleWO = (wo) => setSelectedWOs(prev=>{const n=new Set(prev);n.has(wo)?n.delete(wo):n.add(wo);return n;});
  const toggleExpand = (wo) => setExpandedWOs(prev=>{const n=new Set(prev);n.has(wo)?n.delete(wo):n.add(wo);return n;});
  const toggleAllWOs = () => {
    if (selectedWOs.size===workOrderGroups.length) setSelectedWOs(new Set());
    else setSelectedWOs(new Set(workOrderGroups.map(g=>g.workOrder)));
  };

  const selectedGroups = workOrderGroups.filter(g=>selectedWOs.has(g.workOrder));
  const selectedSamples = selectedGroups.flatMap(g=>g.samples);

  const doPrint = (groups, opts) => {
    const freshSamples = db.getSamples();
    const samples = groups.flatMap(g =>
      g.samples.map(s => freshSamples.find(fs => fs.id === s.id) || s)
    );
    const html = REPORT_FORMATS[reportFormat].build(samples, opts);
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.onload = () => win.print();
  };

  const handlePrint = (groupsOverride) => {
    const groups = groupsOverride||selectedGroups;
    if (groups.length===0){toast('error','Select at least one work order');return;}
    setPrintModal(groups);
  };

  // Reuse the established work-order report pipeline with a deliberately
  // narrowed group when the customer needs one report per sample.
  const oneSampleGroup = (group, sample) => [{
    ...group,
    samples: [sample],
    allComplete: Boolean(sample.completed),
  }];

  const handleExcel = async (groupsOverride) => {
    const groups = groupsOverride||selectedGroups;
    if (groups.length===0){toast('error','Select at least one work order');return;}
    setExporting(true);
    try { await exportToExcel(groups, toast); }
    catch(e){toast('error','Export failed: '+e.message);}
    finally{setExporting(false);}
  };

  const openEmailModal = (groupsOverride) => {
    const groups = groupsOverride||selectedGroups;
    if (groups.length===0){toast('error','Select at least one work order');return;}
    const samples = groups.flatMap(g=>g.samples);
    const first = samples[0];
    const client = first?.client||first?.source||'';
    const savedContacts = normalizeContacts(samples.flatMap(sample =>
      getCompanyContacts(sample.client || sample.source || '', sample)
    ));
    const reportNums = samples.map(s=>s.id);
    const numStr = reportNums.length>1?`${reportNums[0]}..${reportNums[reportNums.length-1]}`:String(reportNums[0]||'');
    const descriptions = [...new Set(samples
      .map(s=>String(s.desc||'').trim())
      .filter(Boolean))];
    const descriptionText = descriptions.length
      ? descriptions.join('; ')
      : (reportNums.length>1 ? `Samples ${numStr}` : `Sample ${numStr}`);
    const subjectText = samples.length > 3 ? 'Continued' : descriptionText;
    setEmailModal({
      groups, samples,
      to: contactEmails(savedContacts),
      savedContacts,
      cc: getApiSettings().defaultCc || '',
      subject: `Sterling Analytical Laboratory Report - ${subjectText}`,
      body: `Dear ${savedContacts[0]?.name||first?.attention||client||'Client'},\n\nPlease find attached your analytical report(s) for ${descriptionText} (Report No${reportNums.length>1?'s':''}: ${numStr}).\n\nIf you have any questions regarding these results, please do not hesitate to contact us at (413) 214-6541 or customerservice@sterlinganalytical.com.\n\nThank you for your business.\n\nSincerely,\nDale Percoski\nLaboratory Supervisor\nSterling Analytical, Inc.\n15 Agawam Avenue, West Springfield, MA 01089\nPhone: (413) 214-6541  |  Fax: (413) 214-6842\nwww.sterlinganalytical.com`,
    });
  };

  // Send via MAPI — opens Outlook desktop with PDF auto-attached
  // Generate PDF in browser using print capture, then send base64 to server
  const generatePDFBase64 = (html) => new Promise((resolve, reject) => {
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:850px;height:1100px;border:none;';
    document.body.appendChild(iframe);

    iframe.onload = () => {
      try {
        // Use html2canvas approach via print
        const iDoc = iframe.contentDocument;
        iDoc.open();
        iDoc.write(html);
        iDoc.close();

        // Wait for images to load then print to PDF
        setTimeout(() => {
          try {
            iframe.contentWindow.focus();
            // Trigger print which the server will capture
            document.body.removeChild(iframe);
            resolve(null); // signal to use server-side Word conversion
          } catch(e) {
            document.body.removeChild(iframe);
            reject(e);
          }
        }, 1500);
      } catch(e) {
        try { document.body.removeChild(iframe); } catch {}
        reject(e);
      }
    };
  });

  const handleSendEmail = async () => {
    if (!emailModal) return;
    if (!emailModal.to) { toast('error', 'Recipient email is required'); return; }
    setSending(true);

    try {
      const reportHTML = REPORT_FORMATS[reportFormat].build(emailModal.samples, {
        reportDate: new Date().toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'}),
        poNumber: ''
      });
      const wo = emailModal.groups.map(g=>g.workOrder).join('-');
      const reportIdentity = emailModal.samples.length === 1
        ? emailModal.samples[0].id
        : wo;
      const fileName = `Sterling_Report_${reportIdentity}_${new Date().toISOString().split('T')[0]}.pdf`;

      const res = await fetch(`${MAPI_URL}/send-mapi`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: emailModal.to, cc: emailModal.cc||'',
          subject: emailModal.subject, body: emailModal.body,
          reportHTML, fileName,
          workOrders: emailModal.groups.map(g=>g.workOrder),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast('success', data.cocAttached
          ? '✅ Outlook is opening with the report and COC(s) attached'
          : '✅ Outlook is opening with the report attached');
        setEmailModal(null);
      } else {
        toast('error', data.error || 'Failed to open Outlook');
      }
    } catch (err) {
      toast('error', 'Local email server not running. On the lab PC run: cd server && npm start');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="toolbar">
        <label style={{fontSize:11,color:'#666',fontWeight:600,display:'flex',alignItems:'center',gap:5}}>
          Format:
          <select value={reportFormat} onChange={e=>setReportFormat(e.target.value)}
            style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'5px 8px',fontSize:12,
              outline:'none',color:'#1e3060',fontWeight:600,cursor:'pointer'}}>
            {Object.entries(REPORT_FORMATS).map(([key,f])=>(
              <option key={key} value={key}>{f.label}</option>
            ))}
          </select>
        </label>
        <div className="tb-sep"/>
        <button className="btn btn-primary" onClick={()=>handlePrint()}>🖨 Print / Save PDF</button>
        <button className="btn" onClick={()=>setRefreshTick(t=>t+1)}
          style={{borderColor:'#1a6b42',color:'#1a6b42',fontWeight:600}}
          title="Reload latest results">
          ↺ Refresh
        </button>
        <button className="btn" onClick={()=>handleExcel()} disabled={exporting}>
          {exporting?'⏳ Exporting...':'📊 Export Excel'}
        </button>
        <button className="btn" style={{background:'#0078d4',borderColor:'#0078d4',color:'white'}}
          onClick={()=>openEmailModal()}>
          ✉️ Send Email
        </button>
        <span style={{fontSize:10,display:'flex',alignItems:'center',gap:4}}>
          <span style={{width:8,height:8,borderRadius:'50%',display:'inline-block',
            background:serverStatus==='ready'?'#1a6b42':serverStatus==='unconfigured'?'#e09900':'#b83232'}}/>
          <span style={{color:serverStatus==='ready'?'#1a6b42':serverStatus==='unconfigured'?'#e09900':'#b83232',fontWeight:600}}>
            {serverStatus==='ready'?'Server ready':serverStatus==='unconfigured'?'Needs config':'Server offline'}
          </span>
        </span>
        <div className="tb-sep"/>
        <span className="tb-label">View:</span>
        {['all','completed','pending'].map(m=>(
          <button key={m} className={`btn ${viewMode===m?'btn-primary':''}`}
            onClick={()=>setViewMode(m)} style={{textTransform:'capitalize'}}>{m}</button>
        ))}
        <div className="tb-sep"/>
        <input className="search-input" placeholder="Sample ID"
          value={search.id} onChange={e=>setSearch(p=>({...p,id:e.target.value}))} style={{width:90}}/>
        <input className="search-input" placeholder="Work Order"
          value={search.workOrder} onChange={e=>setSearch(p=>({...p,workOrder:e.target.value}))} style={{width:110}}/>
        <input className="search-input" placeholder="Source / Client"
          value={search.source} onChange={e=>setSearch(p=>({...p,source:e.target.value}))} style={{width:140}}/>
        <input className="search-input" placeholder="Description"
          value={search.desc} onChange={e=>setSearch(p=>({...p,desc:e.target.value}))} style={{width:130}}/>
        {Object.values(search).some(v=>v) && (
          <button className="btn btn-sm" onClick={()=>setSearch({id:'',workOrder:'',source:'',desc:''})}>✕</button>
        )}
        <div className="tb-sep"/>
        <span className="tb-label">Selected:</span>
        <span className="tb-value">{selectedWOs.size} WO</span>
        <span className="tb-label" style={{marginLeft:4}}>({selectedSamples.length} samples)</span>
        {selectedWOs.size>0 && <button className="btn btn-sm" onClick={()=>setSelectedWOs(new Set())}>Clear</button>}
      </div>

      <div className="content">
        <div className="notice notice-info" style={{marginBottom:12}}>
          Samples are grouped by <strong>Work Order</strong> — one Work Order = one Chain of Custody = one report.
          Select work orders then Print, Export, or Send Email.
        </div>

        <div className="panel">
          <div className="panel-header">
            📋 Work Orders ({workOrderGroups.length})
            <label style={{marginLeft:'auto',fontSize:11,fontWeight:400,cursor:'pointer',
              display:'flex',alignItems:'center',gap:5}}>
              <input type="checkbox"
                checked={selectedWOs.size===workOrderGroups.length&&workOrderGroups.length>0}
                onChange={toggleAllWOs} style={{accentColor:'white'}}/>
              Select all
            </label>
          </div>

          <div style={{overflowY:'auto',maxHeight:'calc(100vh - 260px)'}}>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{width:36}}></th>
                  <th style={{width:36}}></th>
                  <th>Work Order</th>
                  <th>Client / Source</th>
                  <th>Samples</th>
                  <th>Test Type</th>
                  <th>Received</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {workOrderGroups.map(group => {
                  const isSelected = selectedWOs.has(group.workOrder);
                  const isExpanded = expandedWOs.has(group.workOrder);
                  const completedCount = group.samples.filter(s=>s.completed).length;
                  const totalCount = group.samples.length;
                  const allDone = completedCount===totalCount;
                  const client = group.samples[0]?.source||group.samples[0]?.client||'';
                  const types = [...new Set(group.samples.map(s=>s.testType))];
                  const earliest = group.samples.reduce((a,s)=>(!a||s.received<a)?s.received:a,null);

                  return (
                    <React.Fragment key={group.workOrder}>
                      <tr className={isSelected?'selected':''}
                        style={{background:isSelected?'#ddeeff':'#f8f9ff',fontWeight:600}}>
                        <td>
                          <input type="checkbox" checked={isSelected}
                            onChange={()=>toggleWO(group.workOrder)}
                            style={{accentColor:'#1e3060'}}/>
                        </td>
                        <td>
                          <button onClick={()=>toggleExpand(group.workOrder)}
                            style={{background:'none',border:'none',cursor:'pointer',
                              fontSize:14,color:'#1e3060',fontWeight:700,padding:'0 2px'}}>
                            {isExpanded?'▼':'▶'}
                          </button>
                        </td>
                        <td><strong className="mono">{group.workOrder}</strong></td>
                        <td>{client}</td>
                        <td>
                          <span style={{background:'#1e3060',color:'white',borderRadius:10,
                            padding:'1px 8px',fontSize:11}}>
                            {totalCount} sample{totalCount!==1?'s':''}
                          </span>
                        </td>
                        <td>
                          {types.map(t=>(
                            <span key={t} className={`badge badge-${t?.toLowerCase()}`}
                              style={{marginRight:3}}>{t}</span>
                          ))}
                        </td>
                        <td>{earliest}</td>
                        <td>
                          {allDone
                            ? <span className="badge badge-complete">All Complete</span>
                            : <span className="badge badge-pending">{completedCount}/{totalCount} done</span>
                          }
                        </td>
                        <td style={{display:'flex',gap:4}}>
                          <button className="btn btn-sm btn-primary"
                            onClick={()=>handlePrint([group])}>🖨 Print</button>
                          <button className="btn btn-sm"
                            onClick={async()=>{setExporting(true);try{await exportToExcel([group],toast);}finally{setExporting(false);}}}>
                            📊 XLS
                          </button>
                          <button className="btn btn-sm"
                            style={{background:'#0078d4',borderColor:'#0078d4',color:'white'}}
                            onClick={()=>openEmailModal([group])}>
                            ✉️ Email
                          </button>
                        </td>
                      </tr>

                      {isExpanded && group.samples.map(s=>(
                        <tr key={s.id} style={{background:'#fafcff'}}>
                          <td></td><td></td>
                          <td style={{paddingLeft:24}}>
                            <span className="mono" style={{fontSize:11,color:'#666'}}>└ </span>
                            <strong className="mono">{s.id}</strong>
                          </td>
                          <td style={{fontSize:11}}>{s.source}</td>
                          <td style={{fontSize:11,color:'#666'}}>{s.desc}</td>
                          <td><span className={`badge badge-${s.testType?.toLowerCase()}`}>{s.testType}</span></td>
                          <td style={{fontSize:11}}>{s.received}</td>
                          <td>
                            {s.completed
                              ? <span className="badge badge-complete">Complete</span>
                              : <span className="badge badge-pending">Pending</span>}
                          </td>
                          <td style={{fontSize:11,color:'#888'}}>
                            <span style={{marginRight:7}} title="Only currently assigned parameters print; historical results remain stored">
                              {getReportParams(s).length} report / {Object.keys(s.results||{}).length} stored
                            </span>
                            <button className="btn btn-sm btn-primary"
                              title={`Print only Sample ${s.id}`}
                              onClick={()=>handlePrint(oneSampleGroup(group, s))}
                              style={{fontSize:10,padding:'2px 7px',marginRight:4}}>
                              🖨 Print
                            </button>
                            <button className="btn btn-sm"
                              title={`Email only Sample ${s.id}`}
                              style={{background:'#0078d4',borderColor:'#0078d4',color:'white',
                                fontSize:10,padding:'2px 7px',marginRight:4}}
                              onClick={()=>openEmailModal(oneSampleGroup(group, s))}>
                              ✉️ Email
                            </button>
                            <button onClick={()=>setNotesModal(s)}
                              title="Add/edit comments and before/after photos for this sample"
                              style={{marginLeft:8,fontSize:10,padding:'2px 7px',borderRadius:3,cursor:'pointer',
                                border: (loadSampleComment(s.id) || loadSamplePhotos(s.id).before || loadSamplePhotos(s.id).after)
                                  ? '1px solid #e0c060' : '1px solid #cdd3e4',
                                background: (loadSampleComment(s.id) || loadSamplePhotos(s.id).before || loadSamplePhotos(s.id).after)
                                  ? '#fff8e8' : 'white',
                                color: '#555'}}>
                              📝 Notes
                            </button>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
                {workOrderGroups.length===0 && (
                  <tr><td colSpan={9} className="text-center text-muted" style={{padding:28}}>
                    No work orders found
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Sample Notes Modal ── */}
      {notesModal && (
        <SampleNotesModal sample={notesModal} onClose={()=>setNotesModal(null)} toast={toast}/>
      )}

      {/* ── Email Modal ── */}
      {emailModal && (
        <div className="modal-overlay" onClick={()=>setEmailModal(null)}>
          <div className="modal" style={{width:640}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h2>✉️ Send Report via Outlook</h2>
              <button className="modal-close" onClick={()=>setEmailModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className={`notice ${serverStatus==='ready'?'notice-success':serverStatus==='unconfigured'?'notice-warn':'notice-warn'}`} style={{marginBottom:12}}>
                {serverStatus==='ready' && <span><strong>✅ Ready:</strong> PDF will be generated automatically and attached to the email, along with this work order's COC scan(s) if any are on file.</span>}
                {serverStatus==='unconfigured' && <span><strong>⚠️ Setup needed:</strong> Copy <code>server/.env.example</code> to <code>server/.env</code> and add your Gmail App Password. See README_EMAIL_SETUP.md.</span>}
                {serverStatus==='offline' && <span><strong>⚠️ Email server offline.</strong> Open a second terminal and run:<br/><code style={{background:'#1e3060',color:'white',padding:'2px 6px',borderRadius:3,fontSize:11}}>cd sterling-lims/server &amp;&amp; npm install &amp;&amp; npm start</code></span>}
                {!serverStatus && <span>Checking email server...</span>}
              </div>

              <div style={{background:'#f0f4ff',border:'1px solid #d0d8f0',borderRadius:4,
                padding:'8px 12px',fontSize:11,marginBottom:12}}>
                <strong>Samples:</strong> {emailModal.samples.map(s=>s.id).join(', ')} &nbsp;|&nbsp;
                <strong>Work Order(s):</strong> {emailModal.groups.map(g=>g.workOrder).join(', ')}
              </div>

              <div style={{display:'flex',flexDirection:'column',gap:10}}>
                {emailModal.savedContacts?.length > 0 && (
                  <div style={{background:'#f7fbf7',border:'1px solid #b9d8be',borderRadius:4,padding:'7px 9px',fontSize:11}}>
                    <strong>Saved company contacts:</strong>{' '}
                    {emailModal.savedContacts.map(contact => `${contact.name || 'Contact'}${contact.email ? ` <${contact.email}>` : ''}`).join('; ')}
                  </div>
                )}
                <div className="form-group">
                  <label>To (Recipient Email)</label>
                  <input value={emailModal.to}
                    onChange={e=>setEmailModal(p=>({...p,to:e.target.value}))}
                    placeholder="client@example.com"
                    style={{border:'1px solid #cdd3e4',borderRadius:4,
                      padding:'6px 8px',fontSize:12,width:'100%',outline:'none'}}/>
                </div>
                <div className="form-group">
                  <label>CC</label>
                  <input value={emailModal.cc}
                    onChange={e=>setEmailModal(p=>({...p,cc:e.target.value}))}
                    placeholder="optional"
                    style={{border:'1px solid #cdd3e4',borderRadius:4,
                      padding:'6px 8px',fontSize:12,width:'100%',outline:'none'}}/>
                </div>
                <div className="form-group">
                  <label>Subject</label>
                  <input value={emailModal.subject}
                    onChange={e=>setEmailModal(p=>({...p,subject:e.target.value}))}
                    style={{border:'1px solid #cdd3e4',borderRadius:4,
                      padding:'6px 8px',fontSize:12,width:'100%',outline:'none'}}/>
                </div>
                <div className="form-group">
                  <label>Message Body</label>
                  <textarea value={emailModal.body}
                    onChange={e=>setEmailModal(p=>({...p,body:e.target.value}))}
                    rows={10}
                    style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'6px 8px',
                      fontSize:12,width:'100%',resize:'vertical',fontFamily:'inherit',outline:'none'}}/>
                </div>
                {serverStatus !== 'ready' && (
                  <div style={{background:'#fff8e8',border:'1px solid #e0c060',borderRadius:4,
                    padding:'8px 12px',fontSize:11,color:'#7a5a00'}}>
                    💡 Start the email server in a second terminal:<br/>
                    <code style={{background:'#1e3060',color:'white',padding:'2px 6px',
                      borderRadius:3,display:'inline-block',marginTop:4}}>
                      cd sterling-lims/server &amp;&amp; npm install &amp;&amp; npm start
                    </code>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button style={{background: sending ? '#666' : '#0078d4',color:'white',
                border:'none',borderRadius:4,padding:'8px 22px',
                cursor: sending ? 'not-allowed' : 'pointer',fontSize:13,fontWeight:700,
                display:'flex',alignItems:'center',gap:6}}
                onClick={handleSendEmail} disabled={sending}>
                {sending ? '⏳ Opening Outlook...' : '✉️ Send via Outlook (with PDF)'}
              </button>
              <button className="btn" onClick={()=>setEmailModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Print Options Modal ── */}
      {printModal && (
        <div className="modal-overlay" onClick={()=>setPrintModal(null)}>
          <div className="modal" style={{width:420}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h2>🖨 Print Options</h2>
              <button className="modal-close" onClick={()=>setPrintModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{fontSize:12,color:'#666',marginBottom:14}}>
                Printing <strong>{printModal.length}</strong> work order{printModal.length!==1?'s':''} —{' '}
                <strong>{printModal.flatMap(g=>g.samples).length}</strong> sample{printModal.flatMap(g=>g.samples).length!==1?'s':''}
              </div>
              <div className="form-group" style={{marginBottom:12}}>
                <label>Report Date</label>
                <input value={printDate} onChange={e=>setPrintDate(e.target.value)}
                  placeholder="e.g. June 7, 2026"
                  style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'7px 10px',
                    fontSize:12,outline:'none',width:'100%',boxSizing:'border-box'}}/>
              </div>
              <div className="form-group">
                <label>PO Number (optional)</label>
                <input value={printPO} onChange={e=>setPrintPO(e.target.value)}
                  placeholder="e.g. PO-2026-0042"
                  style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'7px 10px',
                    fontSize:12,outline:'none',width:'100%',boxSizing:'border-box'}}/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" style={{padding:'8px 24px',fontWeight:700}}
                onClick={()=>{
                  doPrint(printModal, { reportDate: printDate, poNumber: printPO });
                  setPrintModal(null);
                }}>
                🖨 Open Print Dialog
              </button>
              <button className="btn" onClick={()=>setPrintModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
